import React, { useEffect, useMemo, useState } from "react";
import { Block } from "@randajan/jet-react/dom/block";
import { Alert, Box, FormControl, FormControlLabel, FormHelperText, Switch, TextField, Typography } from "@mui/material";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";

import { validateProfileForm } from "../../../../../arc/forms/profileForms.js";
import { Say } from "../../../../../arc/locale/consts.js";
import { meBeam } from "../../../../assets/beams/meBeam";
import { BlimpMuiFormActions } from "../../../../elements/BlimpMuiFormActions/BlimpMuiFormActions";
import { BlimpMuiFormSection } from "../../../../elements/BlimpMuiFormSection/BlimpMuiFormSection";
import { Locale } from "../../../../assets/Locale.js";
import { useBeforeUnload } from "../../../../assets/hooks/useBeforeUnload.jsx";
import { getProfileErrorId, getValidationFieldError, handleProfileSubmitReply, mergeValidation } from "../profileValidation.js";

import "./IdentityForm.scss";

const visibleFields = Object.freeze(["alias", "isPublic"]);
const emptyValues = Object.freeze({});

const createValues = (profile={})=>({
    alias:profile.alias || "",
    isPublic:!!profile.isPublic
});

const touchAllFields = _=>Object.fromEntries(visibleFields.map(field=>[field, true]));

export const IdentityForm = (props)=>{
    const { profile, onCancel, onSaved } = props;
    const say = Locale.use();
    
    const initialValues = useMemo(()=>createValues(profile), [profile]);
    const [ values, setValues ] = useState(initialValues);
    const [ touched, setTouched ] = useState(emptyValues);
    const [ submitState, setSubmitState ] = useState("");
    const [ serverValidation, setServerValidation ] = useState(null);
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    useEffect(()=>{
        setValues(initialValues);
        setTouched(emptyValues);
        setServerValidation(null);
        setSubmitState("");
    }, [initialValues]);

    useBeforeUnload();

    const localValidation = useMemo(()=>validateProfileForm("character", values), [values]);
    const validation = useMemo(()=>mergeValidation(localValidation, serverValidation), [localValidation, serverValidation]);
    const showAllErrors = submitState === "validationError";
    const aliasError = getValidationFieldError(validation, "alias");
    const isPublicError = getValidationFieldError(validation, "isPublic");
    const showAliasError = !!aliasError && (showAllErrors || touched.alias);
    const showIsPublicError = !!isPublicError && showAllErrors;

    const resetServerValidation = _=>{
        setServerValidation(null);
        setSubmitState("");
    };

    const setAlias = event=>{
        setValues(current=>({ ...current, alias:event.target.value }));
        resetServerValidation();
    };

    const setIsPublic = event=>{
        setValues(current=>({ ...current, isPublic:event.target.checked }));
        setTouched(current=>({ ...current, isPublic:true }));
        resetServerValidation();
    };

    const submit = async event=>{
        event.preventDefault();
        const result = validateProfileForm("character", values);
        if (!result.isValid) {
            setServerValidation(null);
            setTouched(touchAllFields());
            setSubmitState("validationError");
            return;
        }
        setIsSubmitting(true);
        try {
            const reply = await meBeam.act.updateCharacter(result.values);
            handleProfileSubmitReply(reply, {
                fields:visibleFields,
                setIsSubmitting,
                setServerValidation,
                setSubmitState,
                setTouched,
                onDone:onSaved
            })
        } catch(err) {
            setSubmitState("submitError");
            setIsSubmitting(false);
        }
    };

    return (
        <Block level={0} className="IdentityForm popScreen" caption={say(profile?.alias ? "profileIdentityPopTitleEdit" : "profileIdentityPopTitleSet")}>
            <Box className="BlimpMuiForm" component="form" onSubmit={submit} noValidate>
                <Typography className="BlimpMuiForm-intro">
                    {say("profileIdentityIntro")}
                </Typography>
                {submitState === "validationError" && (
                    <Alert severity="error">{say("formErrorHighlightedFields")}</Alert>
                )}
                {submitState === "submitError" && (
                    <Alert severity="error">{say("profileIdentityErrorSubmit")}</Alert>
                )}
                <Box className="BlimpMuiForm-sections">
                    <BlimpMuiFormSection bodyClassName="IdentityForm-body" icon={AccountBoxOutlinedIcon} title={say("profileIdentitySectionIdentity")}>
                        <TextField
                            autoComplete="nickname"
                            className="BlimpMuiForm-field"
                            error={showAliasError}
                            fullWidth
                            helperText={showAliasError ? say(getProfileErrorId("alias", aliasError)) : say("profileIdentityHelperAlias")}
                            label={say("profileFieldAlias")}
                            name="alias"
                            onBlur={_=>setTouched(current=>({ ...current, alias:true }))}
                            onChange={setAlias}
                            required={values.isPublic}
                            slotProps={{ htmlInput:{ maxLength:16 } }}
                            value={values.alias}
                            variant="outlined"
                        />
                        <FormControl className={values.isPublic ? "BlimpMuiForm-toggle isChecked" : "BlimpMuiForm-toggle"} error={showIsPublicError}>
                            <FormControlLabel
                                className="BlimpMuiForm-toggleControl"
                                control={<Switch checked={values.isPublic} name="isPublic" onChange={setIsPublic}/>}
                                label={(
                                    <Box className="BlimpMuiForm-toggleLabelWrap">
                                        <Typography className="BlimpMuiForm-toggleLabel">{say("profileIdentityPublicLabel")}</Typography>
                                        <Typography className="BlimpMuiForm-toggleState">
                                            {say(Say.id("profile", "identity", "public", values.isPublic ? "published" : "hidden"))}
                                        </Typography>
                                    </Box>
                                )}
                            />
                            <Typography className="BlimpMuiForm-toggleText">
                                {say("profileIdentityPublicHelp")}
                            </Typography>
                            {showIsPublicError && (
                                <FormHelperText>{say(getProfileErrorId("isPublic", isPublicError))}</FormHelperText>
                            )}
                        </FormControl>
                    </BlimpMuiFormSection>
                </Box>
                <BlimpMuiFormActions
                    onCancel={onCancel}
                    submitDisabled={isSubmitting}
                    submitLabel={
                        isSubmitting
                            ? say("commonSaving")
                            : say(values.alias.trim() ? "profileIdentitySubmit" : "profileIdentitySubmitEmpty")
                    }
                />
            </Box>
        </Block>
    );
};
